/**
 * Conversão do layout NEUTRO (vindo da API) para comandos ESC/POS genéricos (bobina 80 mm).
 * Só comandos do padrão ESC/POS comuns a qualquer marca: inicializar, alinhar, negrito,
 * tamanho, QR Code (GS ( k), avanço e corte. Sem acentos por padrão (máxima compatibilidade);
 * a página de código pode ser trocada sem mexer no layout.
 */
import type { PrintLayout, PrintLine } from "@cafegestor/shared";

const ESC = 0x1b;
const GS = 0x1d;

export type EscPosOptions = {
  /** "ascii" remove acentos (padrão); "cp860" usa a página portuguesa (ESC t 3). */
  encoding?: "ascii" | "cp860";
  cut?: boolean;
};

const CP860: Record<string, number> = {
  Ç: 0x80,
  ü: 0x81,
  é: 0x82,
  â: 0x83,
  ã: 0x84,
  à: 0x85,
  Á: 0x86,
  ç: 0x87,
  ê: 0x88,
  Ê: 0x89,
  è: 0x8a,
  Í: 0x8b,
  Ô: 0x8c,
  ì: 0x8d,
  Ã: 0x8e,
  Â: 0x8f,
  É: 0x90,
  À: 0x91,
  È: 0x92,
  ô: 0x93,
  õ: 0x94,
  ò: 0x95,
  Ú: 0x96,
  ù: 0x97,
  Ì: 0x98,
  Õ: 0x99,
  Ü: 0x9a,
  á: 0xa0,
  í: 0xa1,
  ó: 0xa2,
  ú: 0xa3,
  ñ: 0xa4,
  Ñ: 0xa5,
  º: 0xa7,
  Ó: 0x9f,
  ª: 0xa6,
};

export function stripAccents(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function encode(text: string, encoding: "ascii" | "cp860"): number[] {
  const out: number[] = [];
  for (const ch of encoding === "ascii" ? stripAccents(text) : text) {
    const code = ch.charCodeAt(0);
    if (code >= 0x20 && code < 0x7f) out.push(code);
    else if (encoding === "cp860" && CP860[ch] !== undefined) out.push(CP860[ch]!);
    else out.push(0x3f); // "?"
  }
  return out;
}

/** Quebra o texto em linhas de no máximo `width` colunas (por palavra). */
export function wrap(text: string, width: number): string[] {
  const lines: string[] = [];
  for (const para of text.split("\n")) {
    let cur = "";
    for (const word of para.split(/\s+/).filter(Boolean)) {
      if (word.length > width) {
        // palavra maior que a linha: corta em pedaços; o último segue aberto para a próxima
        if (cur) lines.push(cur);
        let i = 0;
        for (; i + width < word.length; i += width) lines.push(word.slice(i, i + width));
        cur = word.slice(i);
      } else if (!cur) cur = word;
      else if (cur.length + 1 + word.length <= width) cur += ` ${word}`;
      else {
        lines.push(cur);
        cur = word;
      }
    }
    lines.push(cur);
  }
  return lines;
}

export function pairLine(left: string, right: string, width: number): string {
  const space = width - right.length - 1;
  const l = left.length > space ? left.slice(0, Math.max(0, space)) : left;
  return `${l}${" ".repeat(Math.max(1, width - l.length - right.length))}${right}`;
}

function qr(data: string): number[] {
  const bytes = [...new TextEncoder().encode(data)];
  const len = bytes.length + 3;
  return [
    GS,
    0x28,
    0x6b,
    0x04,
    0x00,
    0x31,
    0x41,
    0x32,
    0x00, // modelo 2
    GS,
    0x28,
    0x6b,
    0x03,
    0x00,
    0x31,
    0x43,
    0x05, // tamanho do módulo
    GS,
    0x28,
    0x6b,
    0x03,
    0x00,
    0x31,
    0x45,
    0x30, // correção de erro L
    GS,
    0x28,
    0x6b,
    len & 0xff,
    (len >> 8) & 0xff,
    0x31,
    0x50,
    0x30,
    ...bytes, // armazena
    GS,
    0x28,
    0x6b,
    0x03,
    0x00,
    0x31,
    0x51,
    0x30, // imprime
  ];
}

export function toEscPos(layout: PrintLayout, opts: EscPosOptions = {}): Uint8Array {
  const encoding = opts.encoding ?? "ascii";
  const w = layout.widthChars;
  const out: number[] = [ESC, 0x40];
  if (encoding === "cp860") out.push(ESC, 0x74, 0x03);
  const line = (text: string) => out.push(...encode(text, encoding), 0x0a);
  for (const l of layout.lines as PrintLine[]) {
    switch (l.t) {
      case "text": {
        out.push(ESC, 0x61, l.align === "center" ? 1 : l.align === "right" ? 2 : 0);
        if (l.bold) out.push(ESC, 0x45, 1);
        if (l.size === 2) out.push(GS, 0x21, 0x11);
        const width = l.size === 2 ? Math.floor(w / 2) : w;
        for (const part of wrap(l.text, width)) line(part);
        if (l.size === 2) out.push(GS, 0x21, 0x00);
        if (l.bold) out.push(ESC, 0x45, 0);
        out.push(ESC, 0x61, 0);
        break;
      }
      case "pair":
        if (l.bold) out.push(ESC, 0x45, 1);
        line(pairLine(l.left, l.right, w));
        if (l.bold) out.push(ESC, 0x45, 0);
        break;
      case "rule":
        line("-".repeat(w));
        break;
      case "qr":
        out.push(ESC, 0x61, 1, ...qr(l.data), 0x0a, ESC, 0x61, 0);
        break;
      case "feed":
        out.push(ESC, 0x64, Math.min(10, Math.max(0, l.lines)));
        break;
    }
  }
  if (opts.cut !== false) out.push(GS, 0x56, 0x42, 0x00);
  return Uint8Array.from(out);
}

/** Estilo do cupom (80 mm). Também publicado em /print-receipt.css para a impressão na página. */
export const RECEIPT_CSS =
  "@page{size:80mm auto;margin:3mm}" +
  "body{font-family:ui-monospace,monospace;font-size:11px;width:74mm;margin:0 auto;color:#000;background:#fff}" +
  ".c{text-align:center}.r{text-align:right}.b{font-weight:bold}.g{font-size:1.4em}" +
  ".p{display:flex;justify-content:space-between}" +
  "hr{border:0;border-top:1px dashed #000}" +
  ".q{text-align:center;font-size:0.8em;word-break:break-all}.q img{width:40mm}";

/**
 * HTML de 80 mm (visualizar, imprimir pelo navegador, salvar em PDF, compartilhar), só com classes (sem style inline): a política de segurança da página
 * (style-src 'self') bloquearia estilos inline. `stylesheetHref` liga a folha da própria
 * origem; sem ele, o estilo vai embutido (arquivo avulso para compartilhar).
 */
export function toHtml(
  layout: PrintLayout,
  opts: { qrImage?: (data: string) => string; stylesheetHref?: string } = {},
): string {
  const esc = (s: string) =>
    s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
  const cls = (...c: (string | false | undefined)[]) => {
    const list = c.filter(Boolean).join(" ");
    return list ? ` class="${list}"` : "";
  };
  const body = layout.lines
    .map((l) => {
      switch (l.t) {
        case "text":
          return `<div${cls(l.align === "center" && "c", l.align === "right" && "r", l.bold && "b", l.size === 2 && "g")}>${esc(l.text)}</div>`;
        case "pair":
          return `<div${cls("p", l.bold && "b")}><span>${esc(l.left)}</span><span>${esc(l.right)}</span></div>`;
        case "rule":
          return "<hr>";
        case "qr":
          return opts.qrImage
            ? `<div class="q"><img alt="QR Code" src="${esc(opts.qrImage(l.data))}"></div>`
            : `<div class="q">${esc(l.data)}</div>`;
        case "feed":
          return "<br>".repeat(Math.max(0, Math.min(l.lines, 10)));
      }
    })
    .join("\n");
  const style = opts.stylesheetHref
    ? `<link rel="stylesheet" href="${esc(opts.stylesheetHref)}">`
    : `<style>${RECEIPT_CSS}</style>`;
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${esc(layout.title)}</title>
${style}
</head><body>${body}</body></html>`;
}
